import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_BASE_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api`;

function SectionInfaq() {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({
    totalRaised: 0,
    totalUtilized: 0,
    remaining: 0,
    utilizationPercentage: 0,
    totalDonors: 0,
    activeCampaigns: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [campaignsRes, statsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/infaq/campaigns`),
          fetch(`${API_BASE_URL}/infaq/statistics`),
        ]);

        if (campaignsRes.ok) {
          const campaignsData = await campaignsRes.json();
          setCampaigns(campaignsData);
        }

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats({
            totalRaised: Number(statsData.totalRaised) || 0,
            totalUtilized: Number(statsData.totalUtilized) || 0,
            remaining: Number(statsData.remaining) || 0,
            utilizationPercentage: statsData.utilizationPercentage || 0,
            totalDonors: statsData.totalDonors || 0,
            activeCampaigns: statsData.activeCampaigns || 0,
          });
        }
      } catch (error) {
        console.error('Failed to fetch infaq data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ms-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateProgress = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const getDaysLeft = (endDate) => {
    if (!endDate) return null;
    const diff = new Date(endDate) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <section id="infaq" className="py-12 px-6 md:p-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500">Loading campaigns...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="infaq" className="py-12 px-6 md:p-24 bg-neutral-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-[38px] md:text-[46px] font-[CormorantGaramond] font-bold text-gray-900 mb-4">
            {t('landing.infaq.title')}
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('landing.infaq.subtitle')}
          </p>
        </div>

        {/* Total Statistics */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-2">{t('landing.infaq.totalRaised')}</p>
              <p className="text-2xl md:text-3xl font-bold text-[#0D7330]">
                {formatCurrency(stats.totalRaised)}
              </p>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-2">{t('landing.infaq.totalUtilized')}</p>
              <p className="text-2xl md:text-3xl font-bold text-[#16592E]">
                {formatCurrency(stats.totalUtilized)}
              </p>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-2">{t('landing.infaq.remaining')}</p>
              <p className="text-2xl md:text-3xl font-bold text-[#0D7330]">
                {formatCurrency(stats.remaining)}
              </p>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-2">{t('landing.infaq.totalDonors')}</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900">
                {stats.totalDonors}
              </p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>{t('landing.infaq.utilizationFund')}</span>
              <span className="font-semibold text-[#0D7330]">{stats.utilizationPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-[#0D7330] h-3 rounded-full transition-all duration-500"
                style={{ width: `${stats.utilizationPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Campaign Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {campaigns.map((campaign) => {
            const currentAmount = Number(campaign.currentAmount);
            const targetAmount = Number(campaign.targetAmount);
            const daysLeft = getDaysLeft(campaign.endDate);

            return (
              <div
                key={campaign.id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow"
              >
                {/* Campaign Image */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={campaign.featuredImage}
                    alt={campaign.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Campaign Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {campaign.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {campaign.description}
                  </p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">{t('landing.infaq.collected')}</span>
                      <span className="font-semibold text-[#0D7330]">
                        {formatCurrency(currentAmount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#0D7330] h-2 rounded-full transition-all duration-500"
                        style={{ width: `${calculateProgress(currentAmount, targetAmount)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{t('landing.infaq.target')}: {formatCurrency(targetAmount)}</span>
                      <span>{Math.round(calculateProgress(currentAmount, targetAmount))}%</span>
                    </div>
                  </div>

                  {/* Campaign Stats */}
                  <div className="flex justify-between text-xs text-gray-600 mb-4">
                    <span>{campaign.donorCount || 0} {t('landing.infaq.donors')}</span>
                    <span>{daysLeft !== null ? `${daysLeft} ${t('landing.infaq.daysLeft')}` : ''}</span>
                  </div>

                  {/* Donate Button */}
                  <Link
                    to={`/infaq/campaign/${campaign.slug}`}
                    className="block w-full bg-[#0D7330] text-white text-center py-3 rounded-lg font-semibold hover:bg-[#0a5c26] transition-colors"
                  >
                    {t('landing.infaq.donateNow')}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Transparency Note */}
        <div className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg
                className="w-8 h-8 text-[#0D7330]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {t('landing.infaq.transparentTitle')}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('landing.infaq.transparentDesc')}
              </p>
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{t('landing.infaq.paymentMethod')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{t('landing.infaq.fundReport')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{t('landing.infaq.taxReceipt')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* View All Campaigns Link */}
        <div className="text-center mt-12">
          <Link
            to="/infaq"
            className="inline-flex items-center gap-2 border-2 border-[#0D7330] text-[#0D7330] px-8 py-3 rounded-xl font-semibold hover:bg-[#0D7330] hover:text-white transition-colors"
          >
            {t('landing.infaq.viewAllCampaigns')}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default SectionInfaq;
